import { FC, useCallback, useState, useEffect } from "react";
import DesktopDatePicker from "@mui/lab/DesktopDatePicker";
import DesktopTimePicker from "@mui/lab/DesktopTimePicker";
import { LocalizationProvider } from "@mui/lab";
import AdapterMoment from "@mui/lab/AdapterMoment";
import { TextField } from "@mui/material";
import { Button, Form } from "react-bootstrap";
import moment, { Moment } from "moment-timezone";
import { DATE_TIME_FORMATS } from "constant";
import "assets/css/daterangepicker.css";

export interface DateTimeFilterValue {
    startDate: Moment | null;
    startTime: Moment | null;
    endDate: Moment | null;
    endTime: Moment | null;
}

export interface DateTimeFilterProps {
    value?: DateTimeFilterValue;
    onChange?: (value: DateTimeFilterValue) => void;
    onFilter?: (start: Moment, end: Moment) => void;
    onValidationMessageChange?: (message: string) => void;
    className?: string;
    minDate?: Moment;
    maxDate?: Moment;
}

const DateTimeFilter: FC<DateTimeFilterProps> = ({
    value,
    onChange,
    onFilter,
    onValidationMessageChange,
    className,
    minDate,
    maxDate,
}) => {
    const defaultStartDate = moment().subtract(10, "days");
    const defaultEndDate = moment();
    const [localValue, setLocalValue] = useState<DateTimeFilterValue>({
        startDate: value?.startDate || defaultStartDate,
        startTime: value?.startTime || defaultStartDate,
        endDate: value?.endDate || defaultEndDate,
        endTime: value?.endTime || defaultEndDate,
    });

    const [rangeError, setRangeError] = useState<string>("");

    useEffect(() => {
        if (value) {
            setLocalValue(value);
        }
    }, [value]);

    const combineDateAndTime = useCallback(
        (date: Moment | null, time: Moment | null): Moment | null => {
            if (!date?.isValid()) return null;
            if (!time?.isValid()) return null;
            return moment(date)
                .hour(time.hour())
                .minute(time.minute())
                .second(0)
                .millisecond(0);
        },
        []
    );

    const validateRange = useCallback(
        (next: DateTimeFilterValue) => {
            const start = combineDateAndTime(next.startDate, next.startTime);
            const end = combineDateAndTime(next.endDate, next.endTime);
            const now = moment().second(0).millisecond(0);

            if (!start || !end || !next.startDate || !next.endDate || !next.startTime || !next.endTime) {
                setRangeError("Invalid date and time");
                return false;
            }

            if ((start?.isAfter(now)) || (end?.isAfter(now))) {
                setRangeError(
                    "A future date and time cannot be selected."
                );
                return false;
            }

            if (start && end && !end.isAfter(start)) {
                setRangeError("End date/time must be after start date/time");
                return false;
            }
            if (start && end && end.diff(start, "days", true) > 10) {
                setRangeError("Date range cannot exceed 10 days.");
                return false;
            }
            setRangeError("");
            return true;
        },
        [combineDateAndTime]
    );

    // Keep error in sync even when parent drives `value`.
    useEffect(() => {
        validateRange(localValue);
    }, [localValue, validateRange]);

    useEffect(() => {
        onValidationMessageChange?.(rangeError);
    }, [rangeError, onValidationMessageChange]);

    const handleChange =
        <K extends keyof DateTimeFilterValue>(key: K) =>
            (newValue: DateTimeFilterValue[K]) => {
                const updatedValue: DateTimeFilterValue = {
                    ...localValue,
                    [key]: newValue,
                };
                setLocalValue(updatedValue);
                validateRange(updatedValue);
                onChange?.(updatedValue);
            };

    const handleFilterClick = () => {
        if (!validateRange(localValue)) return;

        const start = combineDateAndTime(
            localValue.startDate,
            localValue.startTime
        );
        const end = combineDateAndTime(localValue.endDate, localValue.endTime);

        if (start && end) {
            onFilter?.(start, end);
        }
    };

    const isInvalidRange = rangeError.length > 0;

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <div className={className}>
                <div className="date-time-filter-row">
                    <div className="date-time-filter-group">
                        <Form.Group className="date-time-filter-field">
                            <Form.Label>Start</Form.Label>
                            <DesktopDatePicker
                                value={localValue.startDate}
                                inputFormat={DATE_TIME_FORMATS.DATE_ONLY}
                                onChange={handleChange("startDate")}
                                minDate={minDate}
                                maxDate={
                                    maxDate || localValue.endDate || undefined
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        id="start-date-picker"
                                        fullWidth
                                        label=""
                                        InputLabelProps={{
                                            shrink: false,
                                        }}
                                    />
                                )}
                            />
                        </Form.Group>
                        <div className="date-time-filter-field">
                            <DesktopTimePicker
                                value={localValue.startTime}
                                onChange={handleChange("startTime")}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        id="start-time-picker"
                                        fullWidth
                                        label=""
                                        InputLabelProps={{
                                            shrink: false,
                                        }}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <div className="date-time-filter-group">
                        <Form.Group className="date-time-filter-field">
                            <Form.Label>End</Form.Label>
                            <DesktopDatePicker
                                value={localValue.endDate}
                                inputFormat={DATE_TIME_FORMATS.DATE_ONLY}
                                onChange={handleChange("endDate")}
                                minDate={
                                    minDate ||
                                    (localValue.startDate ?? undefined)
                                }
                                maxDate={maxDate}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        id="end-date-picker"
                                        fullWidth
                                        label=""
                                        InputLabelProps={{
                                            shrink: false,
                                        }}
                                    />
                                )}
                            />
                        </Form.Group>
                        <div className="date-time-filter-field">
                            <DesktopTimePicker
                                value={localValue.endTime}
                                onChange={handleChange("endTime")}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        id="end-time-picker"
                                        fullWidth
                                        label=""
                                        InputLabelProps={{
                                            shrink: false,
                                        }}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <div className="date-time-filter-actions">
                        <Button
                            className="date-time-filter-btn"
                            variant="primary"
                            onClick={handleFilterClick}
                            disabled={isInvalidRange}
                        >
                            Filter
                        </Button>
                        <span className="date-filter-note">
                            (Max range: 10 days)
                        </span>
                    </div>
                </div>
            </div>
        </LocalizationProvider>
    );
};

export default DateTimeFilter;
